import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, User, Building2, Phone, Mail, Ticket, Save, Loader2, CheckCircle, X, Crown, Percent } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SubscriptionManager from '../components/SubscriptionManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Perfil = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [perfil, setPerfil] = useState(null);
  const [profissoes, setProfissoes] = useState([]);
  
  // Form state
  const [form, setForm] = useState({
    nome_empresa: '',
    telefone: '',
    tipo_profissao: ''
  });
  
  // Cupom state
  const [cupomCode, setCupomCode] = useState('');
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [aplicandoCupom, setAplicandoCupom] = useState(false);
  const [showRemoverCupomModal, setShowRemoverCupomModal] = useState(false);
  const [removendoCupom, setRemovendoCupom] = useState(false);

  useEffect(() => {
    fetchPerfil();
    fetchProfissoes();
  }, []);

  const fetchPerfil = async () => {
    try {
      const token = localStorage.getItem('lembrou_token');
      const res = await axios.get(`${API}/perfil`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerfil(res.data);
      setForm({
        nome_empresa: res.data.nome_empresa || '',
        telefone: res.data.telefone || '',
        tipo_profissao: res.data.tipo_profissao || ''
      });
    } catch (error) {
      toast.error('Erro ao carregar perfil');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfissoes = async () => {
    try {
      const res = await axios.get(`${API}/profissoes`);
      setProfissoes(res.data);
    } catch (error) {
      console.error('Erro ao carregar profissões:', error);
    }
  };

  const formatTelefone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits.length ? `(${digits}` : '';
    if (digits.length <= 7) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    if (digits.length <= 11) return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
    return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7,11)}`;
  };

  const handleSave = async () => {
    if (!form.nome_empresa.trim()) {
      toast.error('Nome da empresa é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('lembrou_token');
      await axios.put(`${API}/perfil`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Atualizar localStorage
      const userData = JSON.parse(localStorage.getItem('lembrou_user') || '{}');
      userData.nome_empresa = form.nome_empresa;
      localStorage.setItem('lembrou_user', JSON.stringify(userData));
      
      toast.success('Perfil atualizado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleValidarCupom = async () => {
    if (!cupomCode.trim()) {
      toast.error('Digite o código do cupom');
      return;
    }

    setValidandoCupom(true);
    try {
      const res = await axios.post(`${API}/cupons/validar`, { codigo: cupomCode });
      toast.success(`Cupom válido! ${res.data.desconto_percent}% de desconto`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cupom inválido');
    } finally {
      setValidandoCupom(false);
    }
  };

  const handleAplicarCupom = async () => {
    if (!cupomCode.trim()) {
      toast.error('Digite o código do cupom');
      return;
    }

    setAplicandoCupom(true);
    try {
      const token = localStorage.getItem('lembrou_token');
      const res = await axios.post(
        `${API}/cupons/aplicar`,
        { codigo: cupomCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message);
      setCupomCode('');
      fetchPerfil(); // Recarregar perfil para mostrar cupom ativo
      
      // Se ganhou PRO, atualizar localStorage
      if (res.data.plano_atualizado === 'PRO') {
        const userData = JSON.parse(localStorage.getItem('lembrou_user') || '{}');
        userData.plano = 'PRO';
        localStorage.setItem('lembrou_user', JSON.stringify(userData));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aplicar cupom');
    } finally {
      setAplicandoCupom(false);
    }
  };

  const handleRemoverCupom = async () => {
    setRemovendoCupom(true);
    try {
      const token = localStorage.getItem('lembrou_token');
      await axios.delete(`${API}/cupons/remover`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cupom removido');
      setShowRemoverCupomModal(false);
      fetchPerfil();
    } catch (error) {
      toast.error('Erro ao remover cupom');
    } finally {
      setRemovendoCupom(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Meu Perfil</h1>
              <p className="text-sm text-gray-500">Gerencie seus dados e cupons</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Card de Plano Atual */}
        <Card className="p-6 rounded-2xl border shadow-sm bg-gradient-to-r from-primary/5 to-primary/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                perfil?.plano === 'PRO' ? 'bg-yellow-100' : 'bg-gray-100'
              }`}>
                <Crown className={`w-6 h-6 ${perfil?.plano === 'PRO' ? 'text-yellow-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Plano Atual</p>
                <p className="text-2xl font-bold text-gray-900">{perfil?.plano}</p>
              </div>
            </div>
            {perfil?.cupom_ativo && (
              <div className="text-right">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  <Percent className="w-4 h-4" />
                  {perfil.cupom_ativo.desconto_percent}% OFF
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Cupom: {perfil.cupom_ativo.codigo}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Gerenciador de Assinatura */}
        <SubscriptionManager key={perfil?.cupom_ativo?.codigo || 'no-cupom'} cupomAtivo={perfil?.cupom_ativo} />

        {/* Dados da Conta */}
        <Card className="p-6 rounded-2xl border shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Dados da Conta
          </h2>
          
          <div className="space-y-4">
            {/* Email - apenas leitura */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Mail className="w-4 h-4 inline mr-1.5" />
                Email
              </label>
              <Input
                value={perfil?.email || ''}
                disabled
                className="rounded-xl bg-gray-50"
              />
              <p className="text-xs text-gray-400 mt-1">O email não pode ser alterado</p>
            </div>

            {/* CNPJ/CPF - apenas leitura */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Building2 className="w-4 h-4 inline mr-1.5" />
                {perfil?.tipo_usuario === 'PF' ? 'CPF' : 'CNPJ'}
              </label>
              <Input
                value={perfil?.tipo_usuario === 'PF' ? perfil?.cpf : perfil?.cnpj || ''}
                disabled
                className="rounded-xl bg-gray-50"
              />
              <p className="text-xs text-gray-400 mt-1">O {perfil?.tipo_usuario === 'PF' ? 'CPF' : 'CNPJ'} não pode ser alterado</p>
            </div>

            {/* Nome da Empresa - editável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {perfil?.tipo_usuario === 'PF' ? 'Nome Completo' : 'Nome da Empresa'} *
              </label>
              <Input
                value={form.nome_empresa}
                onChange={(e) => setForm({ ...form, nome_empresa: e.target.value })}
                placeholder="Digite o nome"
                className="rounded-xl"
              />
            </div>

            {/* Telefone - editável */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Phone className="w-4 h-4 inline mr-1.5" />
                Telefone
              </label>
              <Input
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: formatTelefone(e.target.value) })}
                placeholder="(11) 99999-9999"
                className="rounded-xl"
                maxLength={15}
              />
            </div>

            {/* Profissão - editável (só para PJ) */}
            {perfil?.tipo_usuario !== 'PF' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Tipo de Profissão
                </label>
                <select
                  value={form.tipo_profissao}
                  onChange={(e) => setForm({ ...form, tipo_profissao: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                >
                  <option value="">Selecione...</option>
                  {profissoes.map((prof) => (
                    <option key={prof.id} value={prof.nome}>
                      {prof.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Botão Salvar */}
            <div className="pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full rounded-xl bg-primary hover:bg-primary-hover text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Cupom de Desconto */}
        <Card className="p-6 rounded-2xl border shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-primary" />
            Cupom de Desconto
          </h2>

          {perfil?.cupom_ativo ? (
            /* Cupom ativo */
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">Cupom Ativo</p>
                    <p className="text-sm text-green-600">
                      <code className="font-mono font-bold">{perfil.cupom_ativo.codigo}</code>
                      {' — '}
                      {perfil.cupom_ativo.desconto_percent}% de desconto
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRemoverCupomModal(true)}
                  className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Remover
                </Button>
              </div>
              <p className="text-xs text-green-600 mt-3">
                Aplicado em: {new Date(perfil.cupom_ativo.aplicado_em).toLocaleDateString('pt-BR')}
              </p>
            </div>
          ) : (
            /* Aplicar novo cupom */
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Tem um cupom de desconto? Digite o código abaixo para aplicar.
              </p>
              <div className="flex gap-2">
                <Input
                  value={cupomCode}
                  onChange={(e) => setCupomCode(e.target.value.toUpperCase())}
                  placeholder="Digite o código do cupom"
                  className="rounded-xl font-mono uppercase flex-1"
                  maxLength={20}
                />
                <Button
                  onClick={handleAplicarCupom}
                  disabled={aplicandoCupom || !cupomCode.trim()}
                  className="rounded-xl bg-green-600 hover:bg-green-700 text-white px-6"
                >
                  {aplicandoCupom ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Aplicar'
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-400">
                Cupons de 100% dão upgrade automático para o Plano PRO
              </p>
            </div>
          )}
        </Card>
      </main>

      {/* Modal de Confirmação - Remover Cupom */}
      {showRemoverCupomModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-md w-full rounded-2xl p-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Remover Cupom?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Deseja remover o cupom <strong>{perfil?.cupom_ativo?.codigo}</strong> da sua conta? 
                Você perderá o desconto de {perfil?.cupom_ativo?.desconto_percent}%.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRemoverCupomModal(false)}
                  disabled={removendoCupom}
                  className="flex-1 rounded-xl"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleRemoverCupom}
                  disabled={removendoCupom}
                  className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white"
                >
                  {removendoCupom ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Removendo...
                    </>
                  ) : (
                    'Sim, Remover'
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Perfil;
